
import React from 'react';
import { 
  Users, 
  FileText, 
  Clock, 
  Calendar, 
  TrendingUp, 
  TrendingDown,
  Wallet
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionsMinimal } from '@/services/subscription';
import { getInvoices } from '@/services/invoiceService';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/hooks/useLanguage';

interface DashboardCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  className?: string;
  isLoading?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  className,
  isLoading = false
}) => {
  return (
    <div className={cn(
      "glass-card p-6 transition-all duration-300 hover:scale-102 group",
      className
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-24 mt-1 mb-1" />
          ) : (
            <h3 className="text-2xl font-bold mt-1 mb-1">{value}</h3>
          )}
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
          {trend && trendValue && !isLoading && (
            <div className="flex items-center mt-2">
              <span className={cn(
                "text-xs font-medium flex items-center",
                trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-muted-foreground'
              )}>
                {trend === 'up' ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : trend === 'down' ? (
                  <TrendingDown className="h-3 w-3 mr-1" />
                ) : null}
                {trendValue}
              </span>
            </div>
          )}
        </div>
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors duration-300">
          {icon}
        </div>
      </div>
    </div>
  );
};

const DashboardCards: React.FC = () => {
  const { translate } = useLanguage();
  
  const { data, isLoading: isLoadingSubscriptions } = useQuery({
    queryKey: ['subscriptions-minimal'],
    queryFn: getSubscriptionsMinimal
  });
  
  // Extract subscriptions and MRC from the response
  const subscriptions = data?.subscriptions || [];
  const monthlyRecurringRevenue = data?.totalMRC || 0;
  
  console.log(`Dashboard received totalMRC: ${monthlyRecurringRevenue}`);

  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices-dashboard'],
    queryFn: () => getInvoices()
  });

  // Extract invoices from the returned data structure
  const invoices = invoicesData?.data || [];
  
  // Ensure we have valid subscription data before calculations
  const validSubscriptions = React.useMemo(() => {
    console.log(`Total subscriptions loaded: ${subscriptions.length}`);
    return subscriptions.filter(sub => sub.subscription_id && sub.status);
  }, [subscriptions]);
  
  const totalSubscriptions = validSubscriptions.length;
  
  const activeSubscriptions = validSubscriptions.filter(sub => sub.status === 'active').length;
  console.log(`Active subscriptions counted: ${activeSubscriptions}`);
  
  const activePercentage = totalSubscriptions > 0 
    ? Math.round((activeSubscriptions / totalSubscriptions) * 100) 
    : 0;
  
  const pendingActivations = validSubscriptions.filter(sub => 
    sub.status === 'registered' && !sub.activation_date
  ).length;
  
  const now = new Date();
  const next30Days = new Date();
  next30Days.setDate(now.getDate() + 30);
  
  const scheduledTerminations = validSubscriptions.filter(sub => 
    sub.termination_date && 
    new Date(sub.termination_date) > now && 
    new Date(sub.termination_date) <= next30Days
  ).length;
  
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  const thisMonthInvoices = invoices.filter(invoice => {
    const invoiceDate = new Date(invoice.invoice_date);
    return invoiceDate.getMonth() === currentMonth && 
           invoiceDate.getFullYear() === currentYear;
  });
  
  const lastMonthInvoices = invoices.filter(invoice => {
    const invoiceDate = new Date(invoice.invoice_date);
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    return invoiceDate.getMonth() === lastMonth && 
           invoiceDate.getFullYear() === lastMonthYear;
  });
  
  const thisMonthRevenue = thisMonthInvoices.reduce((sum, invoice) => 
    sum + invoice.total_amount, 0
  );
  
  const lastMonthRevenue = lastMonthInvoices.reduce((sum, invoice) => 
    sum + invoice.total_amount, 0
  );
  
  const revenueChangePercentage = lastMonthRevenue > 0 
    ? Math.round(((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100) 
    : 0;
  
  // MRC trend (using pre-calculated value from backend)
  const mrcTrend = 'neutral'; // This would require historical data to determine up/down trend
  const mrcTrendValue = translate('dashboard.currentTotal');

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
      <DashboardCard
        title={translate('dashboard.totalSubscriptions')}
        value={totalSubscriptions}
        subtitle={translate('dashboard.activeSubscriptionsRate')}
        icon={<Users className="h-5 w-5" />}
        trend={activePercentage > 0 ? "up" : "down"}
        trendValue={`${activePercentage}% ${translate('dashboard.active')}`}
        isLoading={isLoadingSubscriptions}
      />
      <DashboardCard
        title={translate('dashboard.monthlyRecurringRevenue')}
        value={formatCurrency(monthlyRecurringRevenue)}
        subtitle={translate('dashboard.mrcSubtitle')}
        icon={<Wallet className="h-5 w-5" />}
        trend={mrcTrend}
        trendValue={mrcTrendValue}
        isLoading={isLoadingSubscriptions}
      />
      <DashboardCard
        title={translate('dashboard.monthlyRevenue')}
        value={formatCurrency(thisMonthRevenue)}
        subtitle={translate('dashboard.currentMonth')}
        icon={<FileText className="h-5 w-5" />}
        trend={revenueChangePercentage > 0 ? "up" : revenueChangePercentage < 0 ? "down" : "neutral"}
        trendValue={`${Math.abs(revenueChangePercentage)}% ${translate('dashboard.fromLastMonth')}`}
        isLoading={isLoadingInvoices}
      />
      <DashboardCard
        title={translate('dashboard.pendingActivations')}
        value={pendingActivations}
        subtitle={translate('dashboard.awaitingActivation')}
        icon={<Clock className="h-5 w-5" />}
        isLoading={isLoadingSubscriptions}
      />
      <DashboardCard
        title={translate('dashboard.scheduledTerminations')}
        value={scheduledTerminations}
        subtitle={translate('dashboard.next30Days')}
        icon={<Calendar className="h-5 w-5" />}
        className="border-amber-200 dark:border-amber-950"
        isLoading={isLoadingSubscriptions}
      />
    </div>
  );
};

export default DashboardCards;
